import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import style from './index.pcss';
import { useClassnames } from 'hook/use-classnames';
import Button from 'component/button';
import { useCancelTokens } from 'component/core/cancel-token';
import useRequestBill from './hooks/use-request-bill';
import useRequestFraud from './hooks/use-request-fraud';
import Error from 'component/error';
import Loader from 'component/loader';
import { formatTime, parseVideoStartTime } from './utils';
import { auth } from 'component/api/auth';
import { reset } from 'component/user/actions';

import styleVideo from './video.pcss';

import { IOperation } from 'component/api/types/api/main/get-bill/get/code-200';

export const Antifrod: React.FC = () => {
    const cn = useClassnames(style, styleVideo);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [tokenBill, tokenUpdateBill] = useCancelTokens(2);

    const videoRef = useRef<HTMLVideoElement>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const checkListRef = useRef<HTMLDivElement>(null);
    const operationRefs = useRef<Array<(HTMLDivElement | null)>>([]);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [activeOperationIndices, setActiveOperationIndices] = useState<Array<number>>([]);

    const [showModal, setShowModal] = useState<'BILL_FRAUD' | 'OPERATION_FRAUD' | null>(null);
    const [operationId, setOperationId] = useState<number | null>(null);
    const [violationFraudId, setViolationFraudId] = useState<number>(0);
    const [violationComment, setViolationComment] = useState('');

    const [sortedOperations, setSortedOperations] = useState<Array<IOperation>>([]);

    const {
        data: dataBill,
        pending: pendingBill,
        error: errorBill,
        requestBill,
        requestBillChecked
    } = useRequestBill();
    const {
        data: fraudList,
        pending: pendingFraud,
        pendingChange,
        error: errorFraud,
        requestFraudList,
        requestOperationFraudAdd,
        requestOperationFraudDelete,
        requestBillFraudAdd,
        requestBillFraudDelete
    } = useRequestFraud();

    useEffect(() => {
        requestFraudList();

        return () => {
            tokenBill.remove();
            tokenUpdateBill.remove();
        };
    }, []);

    const onGetBill = () => {
        requestBill();
    };

    const completeBill = () => {
        dataBill && requestBillChecked(dataBill.bill.id);
    };

    const addOperationFraud = (operation_id: number, fraud_action_id: number, comment: string) => {
        const bill_id = dataBill?.bill.id || 0;
        requestOperationFraudAdd(operation_id, fraud_action_id, comment, () => {
            requestBill(bill_id);
        });
    };

    const deleteOperationFraud = (operation_id: number, fraud_id: number) => {
        const bill_id = dataBill?.bill.id || 0;
        requestOperationFraudDelete(operation_id, fraud_id, () => {
            requestBill(bill_id);
        });
    };

    const addBillFraud = (fraud_action_id: number, comment: string) => {
        const bill_id = dataBill?.bill.id || 0;
        requestBillFraudAdd(bill_id, fraud_action_id, comment, () => {
            requestBill(bill_id);
        });
    };

    const deleteBillFraud = (fraud_id: number) => {
        const bill_id = dataBill?.bill.id || 0;
        requestBillFraudDelete(bill_id, fraud_id, () => {
            requestBill(dataBill?.bill.id);
        });
    };

    const onLogout = () => {
        auth.clear();
        dispatch(reset());
        navigate('/login');
    };

    /* */

    useEffect(() => {
        const video = videoRef.current;
        if (!video) { return; }

        const handleLoadedMetadata = () => setDuration(video.duration);
        video.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [dataBill?.bill.video_fragment?.file_url]);

    useEffect(() => {
       setSortedOperations(
           dataBill && [...dataBill.operations].sort((a, b) => a.timeSec - b.timeSec) || []
       );
    }, [dataBill]);

    const videoStartOffset = useMemo(() => {
        const fileName = dataBill?.bill.video_fragment?.file_url?.split('/').pop();

        return parseVideoStartTime(fileName);
    }, [dataBill?.bill.video_fragment?.file_url]);

    useEffect(() => {
        setCurrentTime(
            videoStartOffset
        );
    }, [videoStartOffset]);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    // === Синхронизация времени ===
    useEffect(() => {
        const video = videoRef.current;
        if (!video) { return; }

        const handleTimeUpdate = () => {
            const time = video.currentTime + videoStartOffset;
            setCurrentTime(time);

            const activeIndices: Array<number> = [];
            let activeSlotTime: number | null = null;

            // Находим самый поздний op.time <= currentTime
            for (const op of sortedOperations) {
                if (op.timeSec <= time + 0.3) {
                    activeSlotTime = op.timeSec;
                } else {
                    break; // дальше не ищем — массив отсортирован
                }
            }

            if (activeSlotTime === null) {
                setActiveOperationIndices([]);

                return;
            }

            // Подсвечиваем все операции с этим временем
            for (let i = 0; i < sortedOperations.length; i++) {
                if (Math.abs(sortedOperations[i].timeSec - activeSlotTime) < 0.001) {
                    activeIndices.push(i);
                }
            }

            setActiveOperationIndices(activeIndices);
        };

        video.addEventListener('timeupdate', handleTimeUpdate);

        return () => {
            video.removeEventListener('timeupdate', handleTimeUpdate);
        };
    }, [sortedOperations, videoStartOffset]);

    useEffect(() => {
        if (activeOperationIndices.length === 0) { return; }
        const firstActiveIndex = activeOperationIndices[0];
        const targetEl = operationRefs.current[firstActiveIndex];
        const container = checkListRef.current;

        if (!targetEl || !container) { return; }

        const containerRect = container.getBoundingClientRect();
        const targetRect = targetEl.getBoundingClientRect();

        const isVisible =
            targetRect.top >= containerRect.top &&
            targetRect.bottom <= containerRect.bottom;

        if (!isVisible) {
            targetEl.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [activeOperationIndices]);

    // === Управление воспроизведением ===
    const togglePlayPause = useCallback(() => {
        const video = videoRef.current;
        if (!video) { return; }
        if (isPlaying) {
            video.pause();
            setIsPlaying(false);
        } else {
            const playPromise = video.play();
            playPromise
                .then(() => {
                    setIsPlaying(true);
                })
                .catch(() => {
                setIsPlaying(false);
            });
        }
    }, [isPlaying]);

    // === Переход по времени ===
    const seekTo = useCallback((time: number) => {
        const video = videoRef.current;
        if (!video) { return; }
        video.currentTime = time;
    }, [videoStartOffset]);

    const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const progress = progressRef.current;
        const video = videoRef.current;

        if (!progress || !video || !duration) { return; }

        const rect = progress.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const ratio = Math.max(0, Math.min(1, clickX / rect.width));
        video.currentTime = ratio * duration;
    }, [duration]);

    // === Модальное окно ===
    const openViolationModalBill = () => {
        if (isPlaying) {
            togglePlayPause();
        }
        setShowModal('BILL_FRAUD');
        setOperationId(null);
        setViolationComment('');
        setViolationFraudId(fraudList[0]?.id || 0);
    };

    const openViolationModalOperation = (operation_id: number) => {
        if (isPlaying) {
            togglePlayPause();
        }
        setShowModal('OPERATION_FRAUD');
        setOperationId(operation_id);
        setViolationComment('');
        setViolationFraudId(fraudList[0]?.id || 0);
    };

    const closeModal = () => {
        setShowModal(null);
    };

    const addViolation = () => {
        if (!violationComment.trim()) {
            return;
        }

        showModal === 'BILL_FRAUD'
            ? addBillFraud(violationFraudId, violationComment)
            : addOperationFraud(operationId || 0, violationFraudId, violationComment);

        closeModal();
    };

    const addFraudToOperation = (operation_id: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        openViolationModalOperation(operation_id);
    };

    const removeFraudFromOperation = (operation_id: number, fraud_id: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        deleteOperationFraud(operation_id, fraud_id);
    };

    const removeBillFraud = (fraud_id: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        deleteBillFraud(fraud_id);
    };

    /* */

    const elVideo = useMemo(() => {
        return (
            <video
                ref={videoRef}
                src={dataBill?.bill.video_fragment?.file_url ?? ''}
                preload="metadata"
                className={cn('video')}
            />
        );
    }, [dataBill?.bill.video_fragment?.file_url]);

    const elModal = useMemo(() => {
        if (showModal) {
            return (
                <div className={cn('modalOverlay')} onClick={closeModal}>
                    <div
                        className={cn('modal')}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        <h3>Добавить нарушение</h3>
                        <p>Время: <strong>{formatTime(currentTime)}</strong></p>
                        <div className={cn('modalField')}>
                            <label>Тип нарушения:</label>
                            <select
                                value={violationFraudId}
                                onChange={(e) => setViolationFraudId(Number(e.target.value))}
                                className={cn('select')}
                            >
                                {fraudList.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={cn('modalField')}>
                            <label>Комментарий:</label>
                            <textarea
                                value={violationComment}
                                onChange={(e) => {
                                    setViolationComment(e.target.value);
                                }}
                                placeholder="Опишите нарушение..."
                                className={cn('textarea')}
                                rows={3}
                            />
                        </div>
                        <div className={cn('modalActions')}>
                            <button onClick={closeModal} className={cn('btn', 'btn--secondary')} disabled={pendingChange}>
                                Отмена
                            </button>
                            <button
                                onClick={addViolation}
                                disabled={!violationComment.trim() || pendingChange}
                                className={cn('btn', 'btn--danger')}
                            >
                                Добавить
                            </button>
                        </div>
                    </div>
                </div>
            );
        }
    }, [showModal, currentTime, fraudList, violationFraudId, violationComment, pendingChange]);

    const elCheckDashboard = () => {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%'
                }}
            >
                <Button
                    onClick={onGetBill}
                    className={cn('antifrod__btn-take-work')}
                    disabled={pendingBill}
                    isLoading={pendingBill}
                >
                    Взять чек в работу
                </Button>
                <Button
                    onClick={onLogout}
                    className={cn('antifrod__btn-logout')}
                    disabled={pendingBill}
                    isLoading={pendingBill}
                    isSecondary={true}
                >
                    Выйти
                </Button>
            </div>
        );
    };

    const elContent = () => {
        if (pendingFraud) {
            return <Loader text="Загрузка списка нарушений..." />;
        }

        if (dataBill === null) {
            return elCheckDashboard();
        }

        if (!dataBill || Object.keys(dataBill).length === 0) {
            return (
                <div className={cn('antifrod__info')}>
                    Чеков для работы не найдено!
                </div>
            );
        }

        const progressPercent = duration ? ((currentTime - videoStartOffset) / duration) * 100 : 0;

        return (
            <div className={cn('container')}>
                <div className={cn('videoSection')}>
                    {/* === ВИДЕО + КОНТРОЛЫ === */}
                    <div className={cn('videoWrapper')}>
                        {elVideo}

                        {/* Таймлайн */}
                        <div
                            className={cn('progressBar')}
                            ref={progressRef}
                            onClick={handleProgressClick}
                        >
                            <div className={cn('progressFill')} style={{width: `${progressPercent}%`}} />
                            <div className={cn('progressHandle')} style={{left: `${progressPercent}%`}} />
                        </div>

                        <div className={cn('controls')}>
                            <button
                                onClick={openViolationModalBill}
                                className={cn('btn', 'btn--danger')}
                                disabled={pendingChange}
                            >
                                Нарушение
                            </button>
                            <button onClick={togglePlayPause} className={cn('btn')} disabled={pendingChange}>
                                {isPlaying ? 'Пауза' : 'Воспроизвести'}
                            </button>
                            <select
                                className={cn('select', 'select-ui')}
                                value={playbackRate}
                                onChange={(e) => setPlaybackRate(Number(e.target.value))}
                                disabled={pendingChange}
                            >
                                <option value={0.25}>0.25×</option>
                                <option value={0.5}>0.5×</option>
                                <option value={1}>1×</option>
                                <option value={1.5}>1.5×</option>
                                <option value={2}>2×</option>
                                <option value={4}>4×</option>
                            </select>
                            <span className={cn('time')}>
                                {formatTime(currentTime)} / {formatTime(duration + videoStartOffset)}
                            </span>
                            <button
                                onClick={completeBill}
                                className={cn('btn', 'btn--success')}
                                disabled={pendingChange}
                            >
                                Закрыть чек
                            </button>
                        </div>

                        <div className={cn('info')}>
                            <span>Чек #{dataBill.bill.id || '---'}</span>
                            <span>от {dataBill.bill.check_date || 'дата не известна'}</span>
                            <span>Одноплатник #{dataBill.bill.cash_register_ip || '---'}</span>
                            <span>Осталось чеков: {dataBill.bills_pending || '---'}</span>
                        </div>
                    </div>

                    {/* === НАРУШЕНИЯ (теперь снизу, скролл) === */}
                    <div className={cn('violationsContainer')}>
                    <h3>Нарушения ({dataBill?.bill.fraud.length || 0})</h3>
                        {dataBill?.bill.fraud.length === 0 ? (
                            <p className={cn('noViolations')}>Нарушений не найдено</p>
                        ) : (
                            <div className={cn('violationsList')}>
                                {dataBill?.bill.fraud.map((v, i) => (
                                    <div key={`violation-${i}`} className={cn('violationItem')}>
                        <span className={cn('violationType')}>
                            {v.fraud_action_name}
                        </span>
                                        {v.comment && (
                                            <span className={cn('violationComment')}>
                                {v.comment}
                            </span>
                                        )}
                                        <button
                                            className={cn('violationRemoveBtn')}
                                            onClick={removeBillFraud(v.id)}
                                            title="Удалить нарушение"
                                            disabled={pendingChange}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div ref={checkListRef} className={cn('checkSection')}>
                    <h3>Чек операций</h3>
                    <div className={cn('checkList')}>
                        {sortedOperations.map((op, i) => (
                            <div
                                key={i}
                                ref={(el) => (operationRefs.current[i] = el)}
                                className={cn('checkItem', {active: activeOperationIndices.includes(i)})}
                                onClick={() => {
                                    seekTo(op.timeSec);
                                }}
                            >
                                <button
                                    className={cn('checkAddBtn', {hasFraud: op.fraud.length > 0})}
                                    onClick={addFraudToOperation(op.id)}
                                    disabled={pendingChange}
                                >
                                    +
                                </button>

                                <span className={cn('checkTime')}>[{op.time_of_operation}]</span>
                                <span className={cn('checkCount')}>{op.count}</span>
                                <span className={cn('checkSum')}>{op.total_good_price}</span>
                                <span className={cn('checkName')}>{op.good_name}</span>

                                {op.fraud.length > 0 && (
                                    <div className={cn('checkNote')}>
                                        {op.fraud.map((fraudItem) => (
                                            <div
                                                key={`fraud-operation-${op.id}-${fraudItem.id}`}
                                                className={cn('fraudItem')}
                                            >
                                                <span>{fraudItem.fraud_action_name}</span>
                                                <span>{fraudItem.comment}</span>
                                                <button
                                                    className={cn('fraudRemoveBtn')}
                                                    onClick={removeFraudFromOperation(op.id, fraudItem.id)}
                                                    title="Удалить нарушение"
                                                    disabled={pendingChange}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* === МОДАЛЬНОЕ ОКНО === */}
                {elModal}
            </div>
        );
    };

    const elError = useMemo(() => {
        if (errorBill || errorFraud) {
            return (
                <Error elIcon={true} className={cn('antifrod__error')}>
                    {errorBill || errorFraud}
                </Error>
            );
        }
    }, [errorBill, errorFraud]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1}}>
            {elError}
            {elContent()}
        </div>
    );
};

// tslint:disable-next-line:max-file-line-count
export default Antifrod;
